// 浏览器与后端通信共用的 axios 实例。
//
// 职责（tasks.md T-2.5.1）：
// - 开启 withCredentials=true，确保 HttpOnly session cookie 会被带上
// - 对写操作自动注入 X-CSRF-Token，token 来自内存态 provider
// - 绝不从持久化存储里读取 CSRF token（design.md §5.3）
// - access cookie 401 时静默走 SDK /auth/refresh 一次，成功则重试原请求
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

const MUTATING = new Set(['post', 'put', 'patch', 'delete']);

// SDK 0.4.4+：access JWT 过期时 POST 这里换新 access + refresh。
// 路径相对 baseURL，保持跟 BladeAuth.router 在 /api/v1/auth 下的挂载一致。
const REFRESH_PATH = '/auth/refresh';

type RetriableConfig = AxiosRequestConfig & { _bladeRefreshRetried?: boolean };

// 会话确认失效的标记:仅当 /auth/refresh 返回 401(BladeAuth 用它表示
// refresh token 缺失 / 被拒绝),或 refresh 成功后重放原请求仍 401 时置
// true。refresh 因网络错误 / 5xx / 403(代理、WAF 等访问策略)失败而抛回
// 的原始 401 不带该标记——那些情况不能断定 refresh token 已失效,调用方
// (如 desktopLayoutStore)不应据此登出。
export type BladeAuthAwareError = { bladeSessionExpired?: boolean };

export type CsrfProvider = () => string | null;

export function createHttp(getCsrf: CsrfProvider): AxiosInstance {
  const instance = axios.create({
    baseURL: '/api/v1',
    withCredentials: true,
  });

  instance.interceptors.request.use((config) => {
    const method = (config.method ?? 'get').toLowerCase();
    if (MUTATING.has(method)) {
      const token = getCsrf();
      if (token) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>)['X-CSRF-Token'] = token;
      }
    }
    return config;
  });

  // 多个并发请求同时 401 时共享一次 refresh 调用，避免一窝蜂打 /auth/refresh。
  // refresh 完成后 cookie 已新，所有等待的请求各自 retry 即可。
  let refreshing: Promise<void> | null = null;

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config as RetriableConfig | undefined;
      const status = error.response?.status;

      // 只在以下情况尝试 refresh：
      // - 401 且 server 真的返了响应（network error 不算）
      // - 不是 /auth/refresh 自己（避免死循环）
      // - 这一条原始请求还没重试过（_bladeRefreshRetried 标志位）
      if (
        status !== 401 ||
        !original ||
        original._bladeRefreshRetried ||
        original.url === REFRESH_PATH
      ) {
        if (
          status === 401 &&
          original &&
          (original._bladeRefreshRetried || original.url === REFRESH_PATH)
        ) {
          // refresh 成功后重放仍 401 / refresh 自身 401:会话确认失效。
          (error as BladeAuthAwareError).bladeSessionExpired = true;
        }
        return Promise.reject(error);
      }
      original._bladeRefreshRetried = true;

      try {
        if (!refreshing) {
          refreshing = instance.post(REFRESH_PATH).then(
            () => {
              refreshing = null;
            },
            (err) => {
              refreshing = null;
              throw err;
            },
          );
        }
        await refreshing;
      } catch (refreshErr) {
        // refresh 失败 → 让原始 401 抛回去。仅当 refresh 返回 401 时打
        // bladeSessionExpired:BladeAuth 的 /auth/refresh 只用 401 表示
        // refresh token 缺失 / 被拒绝(SDK router.py "成功 → 200,失败 →
        // 401")。403 可能来自代理 / WAF 等访问策略,网络错误 / 5xx 是瞬时
        // 故障,都不能据此断定会话失效。
        const refreshStatus = (
          refreshErr as { response?: { status?: number } } | undefined
        )?.response?.status;
        if (refreshStatus === 401) {
          (error as BladeAuthAwareError).bladeSessionExpired = true;
        }
        return Promise.reject(error);
      }

      return instance(original);
    },
  );

  return instance;
}

// APP 默认使用的懒初始化实例。测试可通过 createHttp() + stub provider 自行构造。
let _defaultCsrf: CsrfProvider = () => null;
let _default: AxiosInstance | null = null;

export function installDefaultCsrfProvider(p: CsrfProvider): void {
  _defaultCsrf = p;
  _default = null;
}

export function http(): AxiosInstance {
  if (_default === null) {
    _default = createHttp(_defaultCsrf);
  }
  return _default;
}
